import { CardProduct } from '@/molecules';

import { badgeTransformer, boxTransformer } from '@/lib/graphql/transformers';
import { woosbDiscountTransformer } from '@/lib/graphql/transformers/product/woosb';
import {
  getPercentageDiscount,
  gtmPush,
  hasFreeShipping,
  isBundle,
  isBundleWithoutVariables,
  isConfigurableBundle,
  isMirakl,
  isVariable,
  normalizeItem,
  priceToNumber,
  toCurrency,
} from '@/lib/utils';

import type { Woosb } from '@/lib/graphql/transformers/product/woosb';
import type { TypeCardProductGraphQL } from '@/lib/graphql/types';
import type { TypeProduct } from '@/types';
import type { FunctionComponent } from 'react';

type TypeCardProductWordpressProps = {
  product: any;
  type?: 'COMPLETE' | 'SIMPLE';
  itemListId?: string;
  itemListName?: string;
  itemIndex?: number;
  numColumns: number;
};

const discountFormatter = (
  discount: Woosb['discount'],
  variantDiscount: { value: number | null }[],
) => {
  const maxDiscount = Math.max(
    ...(variantDiscount?.map((i) => (i.value ? i.value : 0)) ?? []),
    discount.type === 'amount'
      ? discount.amount ?? 0
      : discount.percentage ?? 0,
  );
  if (discount.type === 'percentage') return `Hasta -${maxDiscount}%`;
  if (discount.type === 'amount')
    return `Hasta -${toCurrency(maxDiscount ?? 0)}`;
};

/**
 * CardProductWordpress
 */
export const CardProductWordpress: FunctionComponent<
  TypeCardProductWordpressProps
> = ({
  product,
  type = 'COMPLETE',
  itemListId,
  itemListName,
  itemIndex,
  numColumns,
}: TypeCardProductWordpressProps) => {
  const card: TypeCardProductGraphQL =
    product?.caracteriSticasDelProducto?.card;

  const onClick = () => {
    gtmPush({
      event: 'select_item',
      ecommerce: {
        items: [normalizeItem(product, itemListId, itemListName, itemIndex)],
      },
    });
  };

  const isFalseVariable = (product: any) =>
    isVariable(product.type) && product?.variations?.edges?.length === 1;
  const productID = isFalseVariable(product)
    ? product.variations.edges[0].node.databaseId
    : product.databaseId;
  const isConfigurable = isConfigurableBundle(
    product.type,
    product.woosbOptionalProducts,
  );
  const productType: TypeProduct = isFalseVariable(product)
    ? 'SIMPLE'
    : isBundle(product.type) &&
        !isBundleWithoutVariables(product.type, {
          stock: product.stockStatus,
          woosbIds: product.woosbIds,
          bundledItems: product.bundledItems,
        }) &&
        !isConfigurable
      ? 'VARIABLE'
      : product.type;

  product.isMirakl = isMirakl(product?.metaData);

  const sellerInfo: {
    name: string;
    slug: string;
    freeShippingAmount?: string;
    alwaysFree?: boolean;
  } = product.isMirakl
    ? product?.variations?.edges?.[0]?.node?.sellerInfo
    : product?.sellerInfo;

  const hasBox = card?.box && card?.box?.active;
  const imageSize = hasBox ? 'small' : numColumns > 4 ? 'medium' : 'large';

  const discountBundle = woosbDiscountTransformer(product);
  const discount = isConfigurable
    ? discountFormatter(discountBundle, product.customQuantityDiscounts)
    : `-${getPercentageDiscount(
        priceToNumber(product.regularPrice),
        priceToNumber(product.salePrice),
      )}%`;

  return (
    <CardProduct
      type={productType}
      productID={productID}
      href={product.uri}
      stock={product.stockStatus}
      {...(hasBox ? { box: boxTransformer(card.box) } : {})}
      onClick={onClick}
      isConnectif={product.isConnectif ?? false}
      isConfigurable={isConfigurable}
      editable={type === 'COMPLETE'}
      hasFreeShipping={hasFreeShipping(
        product?.shippingClasses?.edges,
        sellerInfo,
        product.price,
      )}
    >
      <CardProduct.Image
        src={card?.image?.sourceUrl ?? product.image?.sourceUrl ?? ''}
        alt={
          card?.image?.altText ??
          product.image?.altText ??
          card?.name ??
          product.name ??
          ''
        }
        {...(card?.imageBackgroundColor
          ? { backgroundColor: card.imageBackgroundColor }
          : {})}
        size={imageSize}
      />

      {product.primaryCategory && (
        <CardProduct.Category href={product.primaryCategory.path}>
          {product.primaryCategory.name}
        </CardProduct.Category>
      )}

      {card?.badge?.active && (
        <CardProduct.Badge {...badgeTransformer(card?.badge, card?.lifeStyle)}>
          {card?.badge?.text}
        </CardProduct.Badge>
      )}

      {type === 'COMPLETE' && product.onSale && (
        <CardProduct.Badge {...badgeTransformer(undefined, card?.lifeStyle)}>
          {discount}
        </CardProduct.Badge>
      )}

      <CardProduct.Name>{card?.name ?? product.name}</CardProduct.Name>

      {sellerInfo && sellerInfo?.name && (
        <CardProduct.Seller
          {...(sellerInfo?.slug && {
            slug: sellerInfo.slug,
          })}
        >
          {sellerInfo.name}
        </CardProduct.Seller>
      )}

      {type === 'COMPLETE' &&
        product.onSale &&
        product.regularPrice &&
        !isConfigurable && (
          <CardProduct.PriceRegular>
            {toCurrency(product.regularPrice?.split('-')?.[0])}
          </CardProduct.PriceRegular>
        )}

      {type === 'COMPLETE' && product.price && !isConfigurable && (
        <CardProduct.PriceDiscounted onSale={!!product.onSale}>
          {toCurrency(product.price?.split('-')?.[0])}
        </CardProduct.PriceDiscounted>
      )}
    </CardProduct>
  );
};

CardProductWordpress.displayName = 'CardProductWordpress';
